import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const DepartmentList = ({ currentUser }) => {
  const canManage = !currentUser?.isSimple;
  const [departments, setDepartments] = useState([]);
  const [operations, setOperations] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    name: '',
    operation: '',
    unit: '',
    status: 'active',
  });

  const [message, setMessage] = useState(null);
  const [collapsedUnits, setCollapsedUnits] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [deptResponse, dashboardResponse] = await Promise.all([
        axios.get('/departments'),
        axios.get('/dashboard'),
      ]);

      if (deptResponse.data.success) setDepartments(deptResponse.data.data || []);
      if (dashboardResponse.data.success) {
        setOperations(dashboardResponse.data.data.operations || []);
        setUnits(dashboardResponse.data.data.units || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar departamentos' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleStatus = async (departmentId, currentStatus) => {
    try {
      await axios.put(`/departments/${departmentId}`, { is_active: !currentStatus });
      setMessage({
        type: 'success',
        text: `Departamento ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`,
      });
      await loadData();
      setTimeout(() => setMessage(null), 2500);
    } catch (error) {
      console.error('Error toggling status:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao alterar status do departamento',
      });
      setTimeout(() => setMessage(null), 4500);
    }
  };

  const handleDelete = async (departmentId, departmentName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o departamento "${departmentName}"?`)) return;

    try {
      await axios.delete(`/departments/${departmentId}`);
      setMessage({ type: 'success', text: 'Departamento excluído com sucesso!' });
      await loadData();
      setTimeout(() => setMessage(null), 2500);
    } catch (error) {
      console.error('Error deleting department:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao excluir departamento',
      });
      setTimeout(() => setMessage(null), 4500);
    }
  };

  const toggleUnit = (operationName, unitName) => {
    const key = `${operationName}__${unitName}`;
    setCollapsedUnits((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Filtrar
  const filteredDepartments = useMemo(() => {
    const term = filters.name.trim().toLowerCase();

    return departments.filter((dept) => {
      if (term) {
        const name = (dept.name || '').toLowerCase();
        const code = (dept.code || '').toLowerCase();
        const desc = (dept.description || '').toLowerCase();
        if (!name.includes(term) && !code.includes(term) && !desc.includes(term)) return false;
      }

      if (filters.operation && dept.operation?.code !== filters.operation) return false;
      if (filters.unit && dept.unit?.code !== filters.unit) return false;

      if (filters.status === 'active' && !dept.is_active) return false;
      if (filters.status === 'inactive' && dept.is_active) return false;

      return true;
    });
  }, [departments, filters]);

  // Agrupar por operação/unidade
  const groupedDepartments = useMemo(() => {
    const grouped = {};
    for (const dept of filteredDepartments) {
      const opKey = dept.operation?.name || 'Sem Operação';
      const unitKey = dept.unit?.name || 'Sem Unidade';

      if (!grouped[opKey]) grouped[opKey] = {};
      if (!grouped[opKey][unitKey]) grouped[opKey][unitKey] = [];
      grouped[opKey][unitKey].push(dept);
    }

    // ordenação consistente
    const sorted = {};
    Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .forEach((op) => {
        sorted[op] = {};
        Object.keys(grouped[op])
          .sort((a, b) => a.localeCompare(b))
          .forEach((unit) => {
            sorted[op][unit] = grouped[op][unit].slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          });
      });

    return sorted;
  }, [filteredDepartments]);

  // Stats
  const totalDepartments = filteredDepartments.length;
  const activeDepartments = filteredDepartments.filter((d) => d.is_active).length;
  const inactiveDepartments = totalDepartments - activeDepartments;

  if (loading) {
    return (
      <div className="goals-list-page department-list-page">
        <div className="dl-container">
          <div className="dl-title-card">
            <h1>Departamentos</h1>
            <p>Carregando lista e filtros…</p>
          </div>
          <div className="dl-loading">
            <div className="spinner-border" role="status" aria-label="Carregando"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="goals-list-page department-list-page">
      <div className="dl-container">
        {/* Alert */}
        {message && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
            <div className="dl-alert-row">
              <span>{message.text}</span>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setMessage(null)} />
            </div>
          </div>
        )}

        {/* Header / Title */}
        <div className="dl-title-card">
          <div className="dl-title-left">
            <h1>
              <i className="fas fa-building" /> Departamentos
            </h1>

            <div className="dl-stats">
              <div className="dl-stat">
                <span className="dl-stat-num">{totalDepartments}</span>
                <span className="dl-stat-label">Total</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-num">{activeDepartments}</span>
                <span className="dl-stat-label">Ativos</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-num">{inactiveDepartments}</span>
                <span className="dl-stat-label">Inativos</span>
              </div>
            </div>
          </div>

          {canManage && (
            <div className="dl-title-right">
              <Link to="/departments/create" className="btn dl-btn dl-btn-primary">
                <i className="fas fa-plus" /> Criar Departamento
              </Link>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="dl-filters-card">
          <div className="dl-filters-head">
            <h3>
              <i className="fas fa-filter" /> Filtros
            </h3>

            <button
              type="button"
              className="btn dl-btn dl-btn-ghost"
              onClick={() => setFilters({ name: '', operation: '', unit: '', status: 'active' })}
            >
              <i className="fas fa-undo" /> Limpar
            </button>
          </div>

          <div className="dl-filters-grid">
            <div className="dl-field">
              <label className="form-label">Nome / Código / Descrição</label>
              <input
                type="text"
                className="form-control"
                placeholder="Busque por nome, código ou descrição…"
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
            </div>

            <div className="dl-field">
              <label className="form-label">Operação</label>
              <select className="form-select" value={filters.operation} onChange={(e) => handleFilterChange('operation', e.target.value)}>
                <option value="">Todas</option>
                {operations.map((op) => (
                  <option key={op.id} value={op.code}>
                    {op.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="dl-field">
              <label className="form-label">Unidade</label>
              <select className="form-select" value={filters.unit} onChange={(e) => handleFilterChange('unit', e.target.value)}>
                <option value="">Todas</option>
                {units.map((u) => (
                  <option key={u.id} value={u.code}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="dl-field">
              <label className="form-label">Status</label>
              <select className="form-select" value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                <option value="active">Apenas ativos</option>
                <option value="inactive">Apenas inativos</option>
                <option value="all">Todos</option>
              </select>
            </div>
          </div>
        </div>

        {/* List */}
        {Object.keys(groupedDepartments).length > 0 ? (
          Object.entries(groupedDepartments).map(([operationName, opUnits]) => {
            const unitCount = Object.keys(opUnits).length;
            const opDeptCount = Object.values(opUnits).reduce((acc, arr) => acc + arr.length, 0);

            return (
              <div key={operationName} className="dl-operation-card">
                <div className="dl-operation-head">
                  <div className="dl-operation-title">
                    <i className="fas fa-layer-group" />
                    <div>
                      <h2>{operationName}</h2>
                      <p>
                        {unitCount} unidade(s) • {opDeptCount} departamento(s)
                      </p>
                    </div>
                  </div>
                </div>

                {Object.entries(opUnits).map(([unitName, depts]) => {
                  const key = `${operationName}__${unitName}`;
                  const isCollapsed = !!collapsedUnits[key];
                  const total = depts.length;
                  const active = depts.filter((d) => d.is_active).length;

                  return (
                    <div key={unitName} className="dl-unit-section">
                      <button
                        type="button"
                        className={`dl-unit-toggle ${isCollapsed ? 'is-collapsed' : 'is-open'}`}
                        onClick={() => toggleUnit(operationName, unitName)}
                        aria-expanded={!isCollapsed}
                      >
                        <div className="dl-unit-left">
                          <i className="fas fa-map-marker-alt" />
                          <span className="dl-unit-name">{unitName}</span>
                        </div>

                        <div className="dl-unit-right">
                          <span className="dl-chip">Total: {total}</span>
                          <span className="dl-chip dl-chip-active">Ativos: {active}</span>
                          <i className="fas fa-chevron-down dl-chevron" aria-hidden="true" />
                        </div>
                      </button>

                      {!isCollapsed && (
                        <ul className="dl-dept-list">
                          {depts.map((dept) => (
                            <li key={dept.id} className="dl-dept-item">
                              <div className="dl-dept-main">
                                <div className="dl-dept-name">
                                  {dept.name}
                                  {dept.code && <span className="dl-code">{dept.code}</span>}
                                </div>

                                {dept.description && <div className="dl-dept-desc">{dept.description}</div>}
                              </div>

                              <div className="dl-dept-actions">
                                <span className={`dl-pill ${dept.is_active ? 'dl-pill-active' : 'dl-pill-inactive'}`}>
                                  {dept.is_active ? 'Ativo' : 'Inativo'}
                                </span>

                                {canManage && (
                                  <>
                                    <Link to={`/departments/${dept.id}/edit`} className="btn dl-btn dl-btn-primary dl-btn-sm">
                                      <i className="fas fa-edit" /> Editar
                                    </Link>

                                    <button
                                      type="button"
                                      className={`btn dl-btn ${dept.is_active ? 'dl-btn-ghost' : 'dl-btn-success'} dl-btn-sm`}
                                      onClick={() => handleToggleStatus(dept.id, dept.is_active)}
                                    >
                                      <i className={`fas fa-${dept.is_active ? 'pause' : 'play'}`} />
                                      {dept.is_active ? 'Desativar' : 'Ativar'}
                                    </button>

                                    <button
                                      type="button"
                                      className="btn dl-btn dl-btn-danger dl-btn-sm"
                                      onClick={() => handleDelete(dept.id, dept.name)}
                                    >
                                      <i className="fas fa-trash" /> Excluir
                                    </button>
                                  </>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        ) : (
          <div className="dl-empty">
            <i className="fas fa-building" />
            <h3>Nenhum departamento encontrado</h3>
            <p>Não há departamentos cadastrados ou que correspondam aos filtros aplicados.</p>
            {canManage && (
              <Link to="/departments/create" className="btn dl-btn dl-btn-primary">
                <i className="fas fa-plus" /> Criar Primeiro Departamento
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentList;
